import {ScrollView, StyleSheet, View} from 'react-native';
import {Title, TextInput, Text, Button} from 'react-native-paper';

import {useAppSettings} from '../components/AppSettings';
import {
  saveSyncStatus,
  sync,
  useSyncAdapterStatus,
} from '../hooks/useGuestyData';
import {useAlerts} from 'react-native-paper-alerts';
import {useQueryClient} from 'react-query';
import {getApiUrl} from '../AppConfig';
import {createHeaders} from '../hooks/useFunctions';

function GuestySynchronization() {
  const Alert = useAlerts();
  const queryClient = useQueryClient();
  let reservationSyncStatus = useSyncAdapterStatus('reservationSync');

  const appSettings = useAppSettings();

  const handleSync = async (syncName: string) => {
    console.log('syncing ' + syncName + ' now');
    try {
      await sync(syncName);
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    } finally {
      await queryClient.invalidateQueries('syncAdapterStatus');
    }
  };

  const handleHygiene = async (
    hygieneTool: string,
    readOnly: boolean = true,
  ) => {
    const accountAPI = `${getApiUrl()}guesty/hygiene/${hygieneTool}/${readOnly}`;
    console.log(`calling ${accountAPI}`);

    const result = await fetch(accountAPI, {
      headers: await createHeaders(),
      method: 'GET',
    });
    try {
      const data = (await result.json()).data;
      if (result.status !== 200 || data.error !== undefined) {
        throw new Error(data.error);
      }
      console.log('hygiene tool result:', JSON.stringify(data, null, 2));
      Alert.alert(
        `'${hygieneTool}' Log`,
        JSON.stringify(data, null, 2),
        [{text: appSettings.t('OK')}],
        {cancelable: true},
      );
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    } finally {
      // TODO: if !readOnly, invalidate reservation-related caches after fix tools
      // await queryClient.invalidateQueries(which ones here?);
    }
  };

  const handleResetSync = async (syncName: string) => {
    console.log('reset sync status for ' + syncName);
    try {
      await saveSyncStatus({
        syncName,
        scannedThroughMs: 0,
        lastExecutedMs: 0,
        processedCountLastRun: 0,
        unprocessedCountLastRun: 0,
        lastExecutedStatus: 'reset',
      });
    } catch (e) {
      Alert.alert('Error', '' + e, [{text: appSettings.t('OK')}]);
    } finally {
      await queryClient.invalidateQueries('syncAdapterStatus');
    }
  };

  return (
    <>
      <ScrollView persistentScrollbar={true} style={[styles.container]}>
        <View style={styles.horizontalCentered}>
          <Title style={styles.title}>Guesty Synchronization Job</Title>
        </View>

        <View style={[styles.content]}>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Reservations Scanned Through: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={new Date(
                reservationSyncStatus?.scannedThroughMs ?? 0,
              ).toLocaleString()}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Reservations Processed Last Scan: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={(reservationSyncStatus?.processedCountLastRun ?? 0) + ''}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Reservations Unprocessed Last Scan: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={(reservationSyncStatus?.unprocessedCountLastRun ?? 0) + ''}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Last Scan Time: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={new Date(
                reservationSyncStatus?.lastExecutedMs ?? 0,
              ).toLocaleString()}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>Last Scan Status: </Text>
            <TextInput
              mode="outlined"
              dense
              editable={false}
              value={reservationSyncStatus?.lastExecutedStatus ?? ''}
            />
          </View>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Button
              mode="outlined"
              compact
              onPress={async () => handleSync('reservationSync')}>
              Run Job Now
            </Button>
            <Button
              mode="outlined"
              compact
              onPress={async () => handleResetSync('reservationSync')}>
              Reset Job Status
            </Button>
          </View>
        </View>
        <View style={styles.horizontalCentered}>
          <Title style={styles.title}>Guesty Data Hygiene Tools</Title>
        </View>

        <View style={[styles.content]}>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Button
              mode="outlined"
              compact
              onPress={async () => handleHygiene('guestyMatchMissing')}>
              Portal Matched Reservations Missing Check
            </Button>
            <Button
              mode="outlined"
              compact
              onPress={async () => handleHygiene('guestyMatchMissing', false)}>
              Portal Matched Reservations Missing Fix
            </Button>
          </View>
        </View>
        <View style={[styles.content]}>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Button
              mode="outlined"
              compact
              onPress={async () =>
                handleHygiene('reservationTransactionsMissing')
              }>
              Portal Reservation Transactions Missing Check
            </Button>
            <Button
              mode="outlined"
              compact
              onPress={async () =>
                handleHygiene('reservationTransactionsMissing', false)
              }>
              Portal Reservation Transactions Missing Fix
            </Button>
          </View>
        </View>
        <View style={[styles.content]}>
          <View style={[styles.horizontalCentered, styles.content]}>
            <Text>&nbsp;</Text>
            <Button
              mode="outlined"
              compact
              onPress={async () => handleHygiene('guestyLocalMissing', false)}>
              Guesty Unsynced Reservation Fix
            </Button>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  touchableRow: {
    paddingHorizontal: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
    padding: 20,
    marginVertical: 20,
  },
  content: {
    paddingHorizontal: 40,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default GuestySynchronization;
